import { apiFetch } from "../api.js";

/**
 * Insurance api-module — Phase 10 (2026-05-08).
 *
 * Mirror types of `shared/src/schemas/insurance.ts`. Web does NOT
 * import from @hearth/shared (5.1 lock + 8.2 server-only-subpath
 * refinement); we keep a parallel browser-safe definition here.
 */

export type PolicyType =
  | "term_life"
  | "whole_life"
  | "universal_life"
  | "disability_short"
  | "disability_long"
  | "umbrella"
  | "auto"
  | "homeowners"
  | "ho6_coop"
  | "renters"
  | "health"
  | "dental"
  | "vision"
  | "long_term_care"
  | "critical_illness"
  | "accident"
  | "pet"
  | "travel"
  | "identity_theft"
  | "cyber"
  | "jewelry_rider"
  | "art_collectibles"
  | "other";

export type PremiumFrequency =
  | "monthly"
  | "quarterly"
  | "semi_annual"
  | "annual"
  | "one_time";

export type PolicyStatus = "active" | "lapsed" | "cancelled" | "expired";

export interface BeneficiaryDesignation {
  name: string;
  relationship: string;
  pct: number;
  is_contingent?: boolean;
  member_id?: string;
}

export interface InsurancePolicy {
  household_id: string;
  policy_id: string;
  policy_type: PolicyType;
  insured_user_id?: string;
  carrier: string;
  policy_number_last4?: string;
  coverage_amount_cents?: number;
  monthly_benefit_cents?: number;
  premium_cents?: number;
  premium_frequency?: PremiumFrequency;
  effective_date: string;
  expiration_date?: string;
  policy_status: PolicyStatus;
  beneficiaries: BeneficiaryDesignation[];
  beneficiaries_updated_at?: string;
  agent_name?: string;
  agent_contact?: string;
  last_reviewed_at?: string;
  linked_bill_id?: string;
  notes?: string;
  s3_object_key?: string;
  s3_uploaded_at?: string;
  original_filename?: string;
  content_type?: string;
  byte_size?: number;
  created_at: string;
  updated_at: string;
}

export interface ListPoliciesResponse {
  policies: InsurancePolicy[];
  count: number;
}

export type CoverageStatus = "adequate" | "borderline" | "gap";

export interface CoverageEvaluation {
  status: CoverageStatus;
  multiple: number;
  recommended_additional_cents: number;
  target_cents: number;
}

export interface CoverageGapResponse {
  ttm_income_cents: number;
  net_worth_cents: number;
  latest_net_worth_date: string | null;
  term_life: CoverageEvaluation & {
    current_coverage_cents: number;
    rule_of_thumb: string;
  };
  disability: CoverageEvaluation & {
    current_monthly_benefit_cents: number;
    rule_of_thumb: string;
  };
  umbrella: CoverageEvaluation & {
    current_coverage_cents: number;
    rule_of_thumb: string;
  };
}

export interface PremiumHistoryResponse {
  history: Array<{ amount_cents: number; observed_at: string }>;
  linked_bill_id: string | null;
  current_amount_cents: number | null;
  recurrence: string | null;
}

export interface PresignedUploadResponse {
  upload_url: string;
  expires_in_seconds: number;
  s3_object_key: string;
  required_headers: Record<string, string>;
}

export interface PresignedDownloadResponse {
  download_url: string;
  expires_in_seconds: number;
}

export interface CreatePolicyInput {
  policy_type: PolicyType;
  insured_user_id?: string | undefined;
  carrier: string;
  policy_number_last4?: string | undefined;
  coverage_amount_cents?: number | undefined;
  monthly_benefit_cents?: number | undefined;
  premium_cents?: number | undefined;
  premium_frequency?: PremiumFrequency | undefined;
  effective_date: string;
  expiration_date?: string | undefined;
  policy_status?: PolicyStatus | undefined;
  beneficiaries?: BeneficiaryDesignation[] | undefined;
  agent_name?: string | undefined;
  agent_contact?: string | undefined;
  notes?: string | undefined;
}

export function listInsurancePolicies(): Promise<ListPoliciesResponse> {
  return apiFetch<ListPoliciesResponse>("/v1/insurance");
}

export function createInsurancePolicy(
  input: CreatePolicyInput,
): Promise<InsurancePolicy> {
  return apiFetch<InsurancePolicy>("/v1/insurance", {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export function updateInsurancePolicy(
  policyId: string,
  patch: Record<string, unknown>,
): Promise<InsurancePolicy> {
  return apiFetch<InsurancePolicy>(
    `/v1/insurance/${encodeURIComponent(policyId)}`,
    { method: "PATCH", body: JSON.stringify(patch) },
  );
}

export function deleteInsurancePolicy(policyId: string): Promise<void> {
  return apiFetch<void>(`/v1/insurance/${encodeURIComponent(policyId)}`, {
    method: "DELETE",
  });
}

export function getCoverageGap(): Promise<CoverageGapResponse> {
  return apiFetch<CoverageGapResponse>("/v1/insurance/coverage-gap");
}

export function getPremiumHistory(
  policyId: string,
): Promise<PremiumHistoryResponse> {
  return apiFetch<PremiumHistoryResponse>(
    `/v1/insurance/${encodeURIComponent(policyId)}/premium-history`,
  );
}

export function getInsuranceUploadUrl(
  policyId: string,
  payload: { filename: string; content_type: string; byte_size?: number },
): Promise<PresignedUploadResponse> {
  return apiFetch<PresignedUploadResponse>(
    `/v1/insurance/${encodeURIComponent(policyId)}/upload-url`,
    { method: "POST", body: JSON.stringify(payload) },
  );
}

export function getInsuranceDownloadUrl(
  policyId: string,
): Promise<PresignedDownloadResponse> {
  return apiFetch<PresignedDownloadResponse>(
    `/v1/insurance/${encodeURIComponent(policyId)}/download-url`,
  );
}

export function linkBillToPolicy(
  policyId: string,
  billId: string,
): Promise<{ policy: InsurancePolicy; bill: Record<string, unknown> }> {
  return apiFetch<{ policy: InsurancePolicy; bill: Record<string, unknown> }>(
    `/v1/insurance/${encodeURIComponent(policyId)}/link-bill`,
    { method: "POST", body: JSON.stringify({ bill_id: billId }) },
  );
}

export function unlinkBillFromPolicy(
  policyId: string,
): Promise<{ unlinked: boolean; policy_id: string; bill_id: string }> {
  return apiFetch<{ unlinked: boolean; policy_id: string; bill_id: string }>(
    `/v1/insurance/${encodeURIComponent(policyId)}/unlink-bill`,
    { method: "POST", body: JSON.stringify({}) },
  );
}
